import React from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import Icon from '@/components/ui/Icon';
import cn from '@/utils/cn';
import CorrelationCountPill from './CorrelationCountPill';
import StandardTreeNode from './StandardTreeNode';
import { ChildrenMap } from './standardsTreeUtils';
import { GradeGroup } from './types';

interface GradeGroupSectionProps {
  childrenMap: ChildrenMap;
  correlationCount: number;
  correlationCountMap: Map<number, number>;
  expandedNodes: Set<number>;
  group: GradeGroup;
  isOpen: boolean;
  matchCount?: number;
  matchIds?: Set<number>;
  searchQuery?: string;
  showCorrelationCounts: boolean;
  visibleIds?: Set<number>;
  onToggleGrade: (grade: string) => void;
  onToggleNode: (id: number) => void;
}

const GradeGroupSection = ({
  childrenMap,
  correlationCount,
  correlationCountMap,
  expandedNodes,
  group,
  isOpen,
  matchCount = 0,
  matchIds,
  searchQuery = '',
  showCorrelationCounts,
  visibleIds,
  onToggleGrade,
  onToggleNode,
}: GradeGroupSectionProps) => {
  const isSearching = searchQuery.trim().length > 0;
  const roots = isSearching && visibleIds
    ? group.roots.filter(r => visibleIds.has(r.id))
    : group.roots;

  return (
    <Collapsible.Root
      open={isSearching || isOpen}
      onOpenChange={() => { if (!isSearching) onToggleGrade(group.grade); }}
    >
      <Collapsible.Trigger asChild>
        <button
          className={cn(
            'tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2',
            'tw-bg-gray-50 tw-border-0',
            'tw-cursor-pointer tw-text-left hover:tw-bg-gray-100',
            'tw-font-semibold tw-text-sm',
          )}
          type="button"
        >
          <Icon icon={(isSearching || isOpen) ? 'ChevronDown' : 'ChevronRight'} size={12} />
          <span>{group.label}</span>
          <span className="tw-text-xs tw-font-normal tw-text-dark-grey">
            {isSearching
              ? `${matchCount} matches · ${group.taggableCount} taggable in subtree`
              : `${group.taggableCount} taggable · ${group.totalCount} total`}
          </span>
          {showCorrelationCounts && (
            <span className="tw-ml-auto tw-mr-1">
              <CorrelationCountPill count={correlationCount} direct={false} />
            </span>
          )}
        </button>
      </Collapsible.Trigger>
      <Collapsible.Content>
        {roots.map(root => (
          <StandardTreeNode
            key={root.id}
            childrenMap={childrenMap}
            correlationCountMap={correlationCountMap}
            expandedNodes={expandedNodes}
            gradeLabel={group.label}
            matchIds={matchIds}
            node={root}
            searchQuery={searchQuery}
            showCorrelationCounts={showCorrelationCounts}
            visibleIds={visibleIds}
            onToggleNode={onToggleNode}
          />
        ))}
      </Collapsible.Content>
    </Collapsible.Root>
  );
};

export default GradeGroupSection;
